import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    PlayCircle, Loader2, AlertTriangle, Video, MonitorPlay,
    Plus, Pencil, EyeOff, Trash2, X, GripVertical, Lock, Upload,
} from 'lucide-react';
import VideoProcedimento from '../VideoProcedimento';
import { COR, COR_ESCURA, COR_CLARA } from './BlocosManual';

// ---------------------------------------------------------------------------
// BIBLIOTECA › VIDEOTECA
// ---------------------------------------------------------------------------
// A segunda prateleira do módulo: os vídeos de procedimento do portal reunidos
// num lugar só. São os MESMOS vídeos que já tocam dentro das telas de trabalho
// (hoje o Lançamento de Petty Cash e o Registro de Nulos, no Financeiro) — aqui
// eles ganham um índice para quem quer assistir SEM estar no meio da tarefa:
// gerente novo aprendendo a rotina, treinamento de turno, conferência de como o
// procedimento deve ser feito.
//
// Desde 2026-09-14 ela não é só um índice: é o CATÁLOGO ADMINISTRÁVEL. Os vídeos
// ficam em PRATELEIRAS (por módulo), podem ser arrastados dentro delas, e o
// `Admin` insere, edita, tira do ar e exclui pela própria tela.
//
// ⚠️ A LISTA VEM DO BANCO (`GET /api/biblioteca/videos` → dtbVideoProcedimento),
//    e é o contrário do acervo de manuais ao lado, que é lista em código. Não é
//    incoerência: manual é conteúdo versionado em PR; vídeo é arquivo que a
//    equipe troca sem deploy.
//
// ⚠️⚠️ PRATELEIRA (`categoria`) NÃO É O MÓDULO DONO (`modulo`), e a diferença é
//    a armadilha deste módulo: o `modulo` manda na PASTA do arquivo no drive,
//    que é ÂNCORA DE LEITURA da URL — por isso ele é IMUTÁVEL e o backend recusa
//    trocá-lo. A `categoria` é só onde o card aparece. É ela que o formulário de
//    editar oferece; o módulo só é escolhido na CRIAÇÃO.
//
// ⚠️ O PLAYER NÃO É NOSSO: cada card usa o `<VideoProcedimento>` compartilhado,
//    apontando para o `apiBase` que veio na listagem (hoje, sempre o router
//    genérico da Biblioteca). É ele que tem o link assinado de 10 min, o retry e
//    o confinamento de pasta no drive. A Videoteca só faz o índice — duplicar
//    aquele miolo é exatamente o que derrubou a produção em 03/09/2026.
//
// ⚠️ CORES: os cards são SIENNA (a cor da Biblioteca), não a cor do módulo dono
//    do vídeo. A tela está dentro da Biblioteca, e a regra do portal é a cor do
//    módulo em que a pessoa está (docs/ARQUITETURA.md › Paleta dos módulos). O
//    módulo dono aparece como ETIQUETA, que é a informação que ela precisa.
// ---------------------------------------------------------------------------

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const usuario = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};

// Nome de exibição da prateleira e do módulo dono — os dois usam o mesmo
// vocabulário, que é o do backend (`PASTA_MODULO`).
//
// ⚠️ FALHA ABERTA de propósito (ao contrário do backend, que falha fechada):
//    chave fora deste mapa aparece com o próprio nome cru em vez de sumir. Aqui
//    o que está em jogo é um rótulo, e esconder um vídeo por falta de tradução
//    seria pior que mostrá-lo sem ela.
const NOME_MODULO = {
    financeiro: 'Financeiro',
    operacional: 'Operacional',
    manutencao: 'Manutenção',
    rh: 'Recursos Humanos',
    treinamento: 'Treinamento',
    chamados: 'Chamados',
};

const rotuloModulo = (m) => NOME_MODULO[m] || m;

// As opções dos seletores saem do mesmo mapa — uma segunda lista aqui divergiria
// do backend no dia em que um módulo ganhasse pasta.
const OPCOES_MODULO = Object.keys(NOME_MODULO);

const VideotecaHub = ({ isMobile }) => {
    const [videos, setVideos] = useState([]);
    // carregando | pronto | erro
    const [estado, setEstado] = useState('carregando');
    // null | { modo: 'novo' | 'editar', video }
    const [formulario, setFormulario] = useState(null);
    const [arrastando, setArrastando] = useState(null);

    const ehAdmin = usuario().perfil === 'Admin';

    const carregar = useCallback(async () => {
        try {
            const r = await fetch('/api/biblioteca/videos', { headers: auth() });
            if (!r.ok) throw new Error();
            const d = await r.json();
            setVideos(d.videos || []);
            setEstado('pronto');
        } catch {
            setEstado('erro');
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // As prateleiras, na ordem em que o backend devolveu (ele já ordena por
    // prateleira e, dentro dela, pela ordem arrastada).
    const prateleiras = useMemo(() => {
        const mapa = new Map();
        for (const v of videos) {
            if (!mapa.has(v.categoria)) mapa.set(v.categoria, []);
            mapa.get(v.categoria).push(v);
        }
        return [...mapa.entries()];
    }, [videos]);

    // -----------------------------------------------------------------
    // ARRASTAR E SOLTAR — o primeiro do portal, com o DnD nativo do HTML5.
    //
    // ⚠️ SEM BIBLIOTECA, de propósito: o portal não tem nenhuma de DnD, e trazer
    //    uma para reordenar um catálogo de poucos vídeos seria peso
    //    desproporcional.
    // ⚠️ O DnD nativo NÃO FUNCIONA NO TOQUE (celular/tablet), e o preço é aceito:
    //    reordenar é ato de `Admin`, feito no PC; a LEITURA da Videoteca no
    //    tablet da loja não é afetada. Se um dia for preciso reordenar fora do
    //    PC, o conserto são setas ↑↓ no card — não uma lib.
    // ⚠️ A ordem é otimista na tela e só depois vai ao banco: arrastar tem de
    //    parecer instantâneo. Se o PATCH falhar, recarregamos e a ordem do banco
    //    (a verdadeira) volta a valer.
    // -----------------------------------------------------------------
    const soltarEm = async (categoria, chaveDestino) => {
        if (!arrastando || arrastando.categoria !== categoria) return;
        const daPrateleira = videos.filter((v) => v.categoria === categoria);
        const origem = daPrateleira.findIndex((v) => v.chave === arrastando.chave);
        const destino = daPrateleira.findIndex((v) => v.chave === chaveDestino);
        if (origem < 0 || destino < 0 || origem === destino) return;

        const nova = [...daPrateleira];
        nova.splice(destino, 0, nova.splice(origem, 1)[0]);
        // Recompõe a lista inteira preservando as outras prateleiras.
        let i = 0;
        setVideos(videos.map((v) => (v.categoria === categoria ? nova[i++] : v)));
        setArrastando(null);

        try {
            const r = await fetch('/api/biblioteca/videos/ordem', {
                method: 'PATCH',
                headers: { ...auth(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ chaves: nova.map((v) => v.chave) }),
            });
            if (!r.ok) throw new Error();
        } catch {
            alert('Não foi possível salvar a ordem. A lista foi recarregada.');
            carregar();
        }
    };

    const tirarDoAr = async (video) => {
        if (!window.confirm(
            `Tirar "${video.titulo}" do ar?\n\nEle some da Videoteca e das telas de trabalho. `
            + 'A chave continua existindo — para voltar, basta subir o arquivo de novo.'
        )) return;
        const r = await fetch(`/api/biblioteca/videos/${video.chave}`, {
            method: 'PATCH',
            headers: { ...auth(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ foraDoAr: true }),
        });
        if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            return alert(d.message || 'Não foi possível tirar o vídeo do ar.');
        }
        carregar();
    };

    const excluir = async (video) => {
        if (!window.confirm(
            `EXCLUIR "${video.titulo}" definitivamente?\n\n`
            + 'A linha do catálogo e o arquivo no drive são apagados. Não há como desfazer.'
        )) return;
        const r = await fetch(`/api/biblioteca/videos/${video.chave}`, {
            method: 'DELETE', headers: auth(),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) return alert(d.message || 'Não foi possível excluir o vídeo.');
        carregar();
    };

    return (
        <div>
            <div style={{
                background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                borderRadius: 16, padding: isMobile ? 24 : 40, color: '#fff',
                marginBottom: 32, position: 'relative', overflow: 'hidden',
                boxShadow: `0 10px 15px -3px ${COR}4d`,
            }}>
                <div style={{ position: 'relative', zIndex: 10 }}>
                    <h2 style={{
                        fontSize: isMobile ? 24 : 32, fontWeight: 800,
                        marginBottom: 12, marginTop: 0, color: '#fff',
                    }}>
                        Videoteca
                    </h2>
                    <p style={{
                        color: '#fde9d3', fontSize: isMobile ? 15 : 18,
                        maxWidth: 650, margin: 0, lineHeight: 1.6,
                    }}>
                        Os vídeos de procedimento do portal num lugar só — os mesmos que aparecem
                        dentro das telas de trabalho, aqui reunidos para assistir com calma.
                    </p>
                    {ehAdmin && (
                        <button
                            onClick={() => setFormulario({ modo: 'novo' })}
                            style={{
                                marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 8,
                                padding: '10px 18px', borderRadius: 10, border: 'none',
                                background: 'rgba(255,255,255,0.18)', color: '#fff',
                                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            <Plus size={17} /> Inserir vídeo
                        </button>
                    )}
                </div>
                <div style={{ position: 'absolute', right: -30, top: -40, opacity: 0.12 }}>
                    <Video size={250} color="#ffffff" />
                </div>
            </div>

            <style>{'.vt-spin{animation:vt-spin 1s linear infinite}@keyframes vt-spin{to{transform:rotate(360deg)}}'}</style>

            {estado === 'carregando' && (
                <div style={{ padding: 40, textAlign: 'center', color: '#78716c', fontSize: 14 }}>
                    <Loader2 size={20} className="vt-spin" />
                    <span style={{ marginLeft: 8 }}>Carregando a videoteca…</span>
                </div>
            )}

            {estado === 'erro' && (
                <div style={{
                    padding: '16px 18px', borderRadius: 12, background: '#fef2f2',
                    border: '1px solid #fecaca', color: '#991b1b', fontSize: 14,
                    display: 'flex', alignItems: 'center', gap: 8,
                }}>
                    <AlertTriangle size={18} /> Não foi possível carregar a lista de vídeos. Recarregue a tela.
                </div>
            )}

            {/* Catálogo vazio é estado NORMAL, não erro: acontece enquanto nenhum
                vídeo tem arquivo no drive. Dizer isso evita que a tela em branco
                chegue à equipe como bug. */}
            {estado === 'pronto' && videos.length === 0 && (
                <p style={{
                    padding: '14px 18px', borderRadius: 12,
                    background: '#fafaf9', border: '1px dashed #d6d3d1',
                    fontSize: 13.5, lineHeight: 1.6, color: '#78716c', margin: 0,
                }}>
                    Nenhum vídeo publicado ainda. Os vídeos de procedimento são gravados pela equipe
                    e aparecem aqui assim que entram no ar — sem precisar de atualização do portal.
                </p>
            )}

            {estado === 'pronto' && prateleiras.map(([categoria, daPrateleira]) => (
                <section key={categoria} style={{ marginBottom: 36 }}>
                    <h3 style={{
                        margin: '0 0 16px', fontSize: 13, fontWeight: 800, color: '#78716c',
                        textTransform: 'uppercase', letterSpacing: 1,
                        display: 'flex', alignItems: 'center', gap: 10,
                    }}>
                        {rotuloModulo(categoria)}
                        <span style={{ flex: 1, height: 1, background: '#e7e5e4' }} />
                        <span style={{ fontWeight: 600, letterSpacing: 0, textTransform: 'none' }}>
                            {daPrateleira.length} {daPrateleira.length === 1 ? 'vídeo' : 'vídeos'}
                        </span>
                    </h3>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                        gap: 24,
                    }}>
                        {daPrateleira.map((v) => (
                            <CardVideo
                                key={v.chave}
                                video={v}
                                ehAdmin={ehAdmin}
                                arrastando={arrastando?.chave === v.chave}
                                onArrastar={() => setArrastando(v)}
                                onSoltar={() => soltarEm(categoria, v.chave)}
                                onEditar={() => setFormulario({ modo: 'editar', video: v })}
                                onSubstituir={() => setFormulario({ modo: 'novo', video: v })}
                                onTirarDoAr={() => tirarDoAr(v)}
                                onExcluir={() => excluir(v)}
                            />
                        ))}
                    </div>
                </section>
            ))}

            {formulario && (
                <FormularioVideo
                    modo={formulario.modo}
                    video={formulario.video}
                    onFechar={() => setFormulario(null)}
                    onSalvo={() => { setFormulario(null); carregar(); }}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// O CARD de um vídeo.
// ---------------------------------------------------------------------------
const CardVideo = ({
    video: v, ehAdmin, arrastando, onArrastar, onSoltar,
    onEditar, onSubstituir, onTirarDoAr, onExcluir,
}) => (
    <div
        // Só o Admin arrasta — para os demais o card é estático.
        draggable={ehAdmin}
        onDragStart={onArrastar}
        onDragOver={(e) => { if (ehAdmin) e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); onSoltar(); }}
        style={{
            background: v.foraDoAr ? '#fafaf9' : '#fff',
            borderRadius: 16, padding: 24,
            border: `1px solid ${v.foraDoAr ? '#d6d3d1' : '#e7e5e4'}`,
            display: 'flex', flexDirection: 'column', gap: 14,
            opacity: arrastando ? 0.4 : 1,
            cursor: ehAdmin ? 'grab' : 'default',
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {ehAdmin && <GripVertical size={16} style={{ color: '#d6d3d1', flexShrink: 0 }} />}
            <div style={{
                width: 46, height: 46, borderRadius: 12, background: COR_CLARA,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
                <PlayCircle size={23} style={{ color: COR }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{
                    fontSize: 11, fontWeight: 800, color: '#a8a29e',
                    textTransform: 'uppercase', letterSpacing: 0.7,
                    display: 'flex', alignItems: 'center', gap: 6,
                }}>
                    {rotuloModulo(v.modulo)}
                    {v.restrito && <Lock size={11} style={{ color: '#a8a29e' }} />}
                </div>
                <h3 style={{ margin: '2px 0 0', fontSize: 18, fontWeight: 800, color: '#1c1917', lineHeight: 1.25 }}>
                    {v.titulo}
                </h3>
            </div>
        </div>

        {/* Onde o procedimento acontece de verdade. É o que responde
            "e onde eu faço isso?" logo depois de a pessoa assistir. */}
        {v.tela && (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 7,
                fontSize: 13.5, lineHeight: 1.5, color: '#57534e', flex: 1,
            }}>
                <MonitorPlay size={15} style={{ color: '#a8a29e', flexShrink: 0 }} />
                <span>{v.tela}</span>
            </div>
        )}

        <div style={{ paddingTop: 12, borderTop: '1px solid #f5f5f4' }}>
            {/* Vídeo fora do ar não tem o que tocar — só o Admin chega a vê-lo,
                e o que ele precisa ali é resubir ou excluir. */}
            {v.foraDoAr ? (
                <div style={{
                    fontSize: 13, color: '#a16207', background: '#fefce8',
                    border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px',
                }}>
                    Fora do ar — sem arquivo no drive.
                </div>
            ) : (
                <VideoProcedimento
                    apiBase={v.apiBase}
                    chave={v.chave}
                    titulo={v.titulo}
                    cor={COR}
                    rotuloBotao="Assistir"
                />
            )}
        </div>

        {ehAdmin && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 4 }}>
                <BotaoAcao icone={Pencil} rotulo="Editar" onClick={onEditar} />
                <BotaoAcao icone={Upload} rotulo="Trocar arquivo" onClick={onSubstituir} />
                {!v.foraDoAr && <BotaoAcao icone={EyeOff} rotulo="Tirar do ar" onClick={onTirarDoAr} />}
                {/* ⚠️ Excluir só aparece para vídeo JÁ FORA DO AR — a chave pode
                    estar escrita numa tela de trabalho, e o backend não tem como
                    saber. Os dois atos separados dão tempo de alguém perceber. */}
                {v.foraDoAr && <BotaoAcao icone={Trash2} rotulo="Excluir" perigo onClick={onExcluir} />}
            </div>
        )}
    </div>
);

const BotaoAcao = ({ icone: Icone, rotulo, onClick, perigo }) => (
    <button
        onClick={onClick}
        style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
            border: `1px solid ${perigo ? '#fecaca' : '#e7e5e4'}`,
            background: perigo ? '#fef2f2' : '#fff',
            color: perigo ? '#991b1b' : '#57534e',
            fontSize: 12, fontWeight: 600,
        }}
    >
        <Icone size={13} /> {rotulo}
    </button>
);

// ---------------------------------------------------------------------------
// O FORMULÁRIO de inserir / editar.
//
// ⚠️ São DOIS modos com regras diferentes, e a diferença não é cosmética:
//    · 'novo'    → manda arquivo (obrigatório) e escolhe o MÓDULO DONO, que
//                  decide a pasta no drive. Com um vídeo existente na mão, vira
//                  "trocar arquivo": a chave e o módulo ficam travados.
//    · 'editar'  → NÃO manda arquivo e NÃO mexe no módulo (o backend recusa).
//                  Mexe na prateleira, no título, na tela e no "restrito".
// ---------------------------------------------------------------------------
const FormularioVideo = ({ modo, video, onFechar, onSalvo }) => {
    const trocandoArquivo = modo === 'novo' && !!video;
    const [dados, setDados] = useState({
        chave: video?.chave || '',
        titulo: video?.titulo || '',
        modulo: video?.modulo || 'financeiro',
        categoria: video?.categoria && video.categoria !== video.modulo ? video.categoria : '',
        tela: video?.tela || '',
        restrito: !!video?.restrito,
    });
    const [arquivo, setArquivo] = useState(null);
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState(null);

    const campo = (k) => (e) => setDados((d) => ({
        ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

    const salvar = async (e) => {
        e.preventDefault();
        setErro(null);
        setSalvando(true);
        try {
            let r;
            if (modo === 'novo') {
                if (!arquivo) throw new Error('Escolha o arquivo do vídeo.');
                const corpo = new FormData();
                corpo.append('video', arquivo);
                corpo.append('chave', dados.chave.trim().toLowerCase());
                corpo.append('titulo', dados.titulo.trim());
                corpo.append('modulo', dados.modulo);
                corpo.append('categoria', dados.categoria);
                corpo.append('tela', dados.tela.trim());
                corpo.append('restrito', dados.restrito ? '1' : '0');
                // ⚠️ Sem `Content-Type`: o navegador põe o `multipart/form-data`
                //    com o boundary. Escrevê-lo à mão quebra o multer.
                r = await fetch('/api/biblioteca/videos', { method: 'POST', headers: auth(), body: corpo });
            } else {
                r = await fetch(`/api/biblioteca/videos/${video.chave}`, {
                    method: 'PATCH',
                    headers: { ...auth(), 'Content-Type': 'application/json' },
                    // ⚠️ O `modulo` NÃO vai no corpo: o backend recusa a
                    //    requisição inteira se ele vier, porque trocá-lo faria o
                    //    vídeo parar de tocar em silêncio.
                    body: JSON.stringify({
                        titulo: dados.titulo.trim(),
                        categoria: dados.categoria || null,
                        tela: dados.tela.trim() || null,
                        restrito: dados.restrito ? 1 : 0,
                    }),
                });
            }
            const d = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(d.message || 'Não foi possível salvar.');
            onSalvo();
        } catch (ex) {
            setErro(ex.message);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div
            onClick={onFechar}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(28,25,23,0.55)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 16, zIndex: 1000,
            }}
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={salvar}
                style={{
                    background: '#fff', borderRadius: 16, padding: 28,
                    width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
                    display: 'flex', flexDirection: 'column', gap: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800, color: '#1c1917' }}>
                        {modo === 'editar' ? 'Editar vídeo'
                            : trocandoArquivo ? 'Trocar o arquivo' : 'Inserir vídeo'}
                    </h3>
                    <button type="button" onClick={onFechar} style={{
                        border: 'none', background: 'none', cursor: 'pointer', color: '#a8a29e', padding: 4,
                    }}>
                        <X size={20} />
                    </button>
                </div>

                {modo === 'novo' && (
                    <Campo rotulo="Chave" ajuda="Identifica o vídeo em tudo — no banco, no arquivo do drive e na tela. Minúsculas, dígitos e hífen. Não muda depois.">
                        <input
                            value={dados.chave} onChange={campo('chave')} required
                            disabled={trocandoArquivo}
                            placeholder="pettycash-lancamento" style={estiloInput(trocandoArquivo)}
                        />
                    </Campo>
                )}

                <Campo rotulo="Título">
                    <input value={dados.titulo} onChange={campo('titulo')} required
                        placeholder="Lançamento Petty Cash" style={estiloInput()} />
                </Campo>

                {modo === 'novo' && (
                    <Campo
                        rotulo="Módulo dono"
                        ajuda="Define a PASTA do arquivo no drive. Não muda depois que o vídeo sobe — para mudar onde o vídeo aparece, use a prateleira."
                    >
                        <select value={dados.modulo} onChange={campo('modulo')}
                            disabled={trocandoArquivo} style={estiloInput(trocandoArquivo)}>
                            {OPCOES_MODULO.map((m) => <option key={m} value={m}>{rotuloModulo(m)}</option>)}
                        </select>
                    </Campo>
                )}

                <Campo
                    rotulo="Prateleira"
                    ajuda="Onde o vídeo aparece na Videoteca. Em branco, ele fica na prateleira do módulo dono."
                >
                    <select value={dados.categoria} onChange={campo('categoria')} style={estiloInput()}>
                        <option value="">(a do módulo dono)</option>
                        {OPCOES_MODULO.map((m) => <option key={m} value={m}>{rotuloModulo(m)}</option>)}
                    </select>
                </Campo>

                <Campo rotulo="Tela" ajuda="Onde o procedimento é feito de verdade. Aparece no card, abaixo do título.">
                    <input value={dados.tela} onChange={campo('tela')}
                        placeholder="Financeiro › Petty Cash › Lançamento" style={estiloInput()} />
                </Campo>

                {modo === 'novo' && (
                    <Campo rotulo="Arquivo" ajuda="MP4 (H.264 + AAC) ou WebM, até 200 MB. Outros formatos não tocam no navegador.">
                        <input type="file" accept="video/mp4,video/webm"
                            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                            required style={{ fontSize: 13.5 }} />
                    </Campo>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13.5, color: '#44403c', cursor: 'pointer' }}>
                    <input type="checkbox" checked={dados.restrito} onChange={campo('restrito')} />
                    Vídeo interno — só o Admin vê
                </label>

                {erro && (
                    <div style={{
                        padding: '10px 14px', borderRadius: 9, background: '#fef2f2',
                        border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, lineHeight: 1.5,
                    }}>
                        {erro}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
                    <button type="button" onClick={onFechar} style={{
                        padding: '10px 18px', borderRadius: 9, border: '1px solid #e7e5e4',
                        background: '#fff', color: '#57534e', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}>
                        Cancelar
                    </button>
                    <button type="submit" disabled={salvando} style={{
                        padding: '10px 20px', borderRadius: 9, border: 'none',
                        background: salvando ? '#a8a29e' : COR, color: '#fff',
                        fontSize: 14, fontWeight: 700, cursor: salvando ? 'default' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                    }}>
                        {salvando && <Loader2 size={15} className="vt-spin" />}
                        {salvando ? 'Enviando…' : 'Salvar'}
                    </button>
                </div>
            </form>
        </div>
    );
};

const estiloInput = (desabilitado) => ({
    width: '100%', padding: '9px 12px', borderRadius: 9,
    border: '1px solid #e7e5e4', fontSize: 14, color: '#1c1917',
    background: desabilitado ? '#fafaf9' : '#fff',
    boxSizing: 'border-box',
});

const Campo = ({ rotulo, ajuda, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label style={{ fontSize: 12.5, fontWeight: 700, color: '#44403c' }}>{rotulo}</label>
        {children}
        {ajuda && <span style={{ fontSize: 12, color: '#a8a29e', lineHeight: 1.45 }}>{ajuda}</span>}
    </div>
);

export default VideotecaHub;
